import { useEffect, useRef } from "react";

// A USB scanner types a whole code with keys 1-30 ms apart. Keys closer than
// this belong to one burst until enough of them confirm a scanner. Keys bound
// for a text field are held back for this long before they land, so a scan
// never shows in the field, not even for a moment.
const BURST_GAP_MS = 50;
// Fast keys in a row that confirm a scanner.
const CONFIRM_KEYS = 4;
// A confirmed scan ends after this much silence, so a late, missing or Tab
// suffix still ends it and a short stall inside the code does not split it.
const SCAN_END_SILENCE_MS = 120;
// Confirmed bursts shorter than this were a fast typist, not a scan, and land
// in the field they were typed in.
const MIN_SCAN_LENGTH = 8;
// Enter or Tab this soon after a scan is its suffix, however late it arrives,
// so it never reaches the modal's Enter shortcut.
const SUFFIX_WINDOW_MS = 500;
// The same code again within this window is the scanner reading it twice.
const DUPLICATE_WINDOW_MS = 2000;

const US_SHIFTED_DIGITS = ")!@#$%^&*(";
const US_SYMBOLS = {
  Minus: ["-", "_"],
  Equal: ["=", "+"],
  BracketLeft: ["[", "{"],
  BracketRight: ["]", "}"],
  Backslash: ["\\", "|"],
  Semicolon: [";", ":"],
  Quote: ["'", '"'],
  Comma: [",", "<"],
  Period: [".", ">"],
  Slash: ["/", "?"],
  Backquote: ["`", "~"],
  Space: [" ", " "],
  NumpadDecimal: [".", "."],
  NumpadDivide: ["/", "/"],
  NumpadSubtract: ["-", "-"],
  NumpadAdd: ["+", "+"],
};

// The character a scanner on its default US layout meant, from the physical
// key it pressed. Unlike event.key, it does not change with the laptop's
// keyboard layout (on Serbian Latin "/" types "-") or with Caps Lock.
const usCharFromCode = ({ code, shiftKey }) => {
  if (/^Key[A-Z]$/.test(code)) {
    return shiftKey ? code[3] : code[3].toLowerCase();
  }
  if (/^Digit\d$/.test(code)) {
    return shiftKey ? US_SHIFTED_DIGITS[code[5]] : code[5];
  }
  if (/^Numpad\d$/.test(code)) return code[6];
  return US_SYMBOLS[code]?.[shiftKey ? 1 : 0] ?? null;
};

// Some scanners send the LF of a CR+LF suffix as Ctrl+J, which opens Chrome's
// downloads on Windows, and CR as Ctrl+M.
const isSuffixKey = (e) =>
  e.key === "Enter" ||
  e.key === "Tab" ||
  (e.ctrlKey && (e.code === "KeyJ" || e.code === "KeyM"));

// Keys that neither edit a field nor move its caret, so held keys keep waiting
// through them. A scanner presses Shift for every capital letter.
const PASSIVE_KEYS = new Set([
  "Shift",
  "Control",
  "Alt",
  "AltGraph",
  "Meta",
  "CapsLock",
  "NumLock",
  "Fn",
  "Dead",
  "Unidentified",
]);

const TEXT_INPUT_TYPES = new Set([
  "text",
  "search",
  "email",
  "tel",
  "url",
  "password",
  "number",
]);

// The focused field a typed key would go into. Checkboxes, buttons and
// read-only inputs (a Select that is not searchable) take no text.
const focusedTextField = () => {
  const el = document.activeElement;
  const takesText =
    el instanceof HTMLTextAreaElement ||
    (el instanceof HTMLInputElement && TEXT_INPUT_TYPES.has(el.type));
  return takesText && !el.readOnly && !el.disabled ? el : null;
};

// Sets the value the way React sees it, so form state follows.
const writeField = (el, value, caret) => {
  const { set } = Object.getOwnPropertyDescriptor(
    Object.getPrototypeOf(el),
    "value"
  );
  set.call(el, value);
  el.dispatchEvent(new Event("input", { bubbles: true }));
  try {
    el.setSelectionRange(caret, caret);
  } catch {
    // Email and number inputs have no caret to place.
  }
};

// Types text into the field the way its keys would have: over the selection,
// with an undo step and the input event React listens for.
const typeIntoField = (el, text) => {
  if (
    document.activeElement === el &&
    document.execCommand("insertText", false, text)
  ) {
    return;
  }
  // The field lost focus meanwhile, so write the text where its caret was.
  const start = el.selectionStart ?? el.value.length;
  const end = el.selectionEnd ?? start;
  writeField(
    el,
    el.value.slice(0, start) + text + el.value.slice(end),
    start + text.length
  );
};

const swallow = (e) => {
  e.preventDefault();
  e.stopImmediatePropagation();
};

const createScanDetector = (onScan) => {
  let burst = null;
  let endTimer = null;
  let lastScanAt = -Infinity;
  let lastPayload = null;
  // The held burst the silence timer last let into its field, with the field's
  // value before it. A busy page can run the timer before keys that were
  // pressed in time, and the first of them takes the burst back to resume it.
  let landedOnSilence = null;

  const finish = (now, { onSilence = false } = {}) => {
    clearTimeout(endTimer);
    endTimer = null;
    const ended = burst;
    burst = null;
    if (!ended) return;

    const { field, typed, physical } = ended;
    // A person typing, not a scan: the keys held back land in their field.
    if (!ended.confirmed || typed.length < MIN_SCAN_LENGTH) {
      if (!field) return;
      if (onSilence && !ended.confirmed) {
        landedOnSilence = {
          burst: ended,
          value: field.value,
          caret: field.selectionStart ?? field.value.length,
        };
      }
      typeIntoField(field, typed);
      return;
    }

    lastScanAt = now;
    const isDuplicate =
      physical === lastPayload?.text &&
      now - lastPayload.at < DUPLICATE_WINDOW_MS;
    if (isDuplicate) return;
    lastPayload = { text: physical, at: now };
    onScan(physical === typed ? [physical] : [physical, typed]);
  };

  const handleKeyDown = (e) => {
    // Scanners never auto-repeat, so a held key is always a person.
    if (e.repeat || e.isComposing) return;
    const now = e.timeStamp;

    if (isSuffixKey(e)) {
      // A scan's suffix, the second key of CR+LF, or a suffix that came after
      // the silence had already ended the scan.
      if (burst?.confirmed || now - lastScanAt < SUFFIX_WINDOW_MS) swallow(e);
      // A person's Enter or Tab acts once the keys typed before it landed.
      if (burst) finish(now);
      return;
    }

    if (e.ctrlKey || e.metaKey || e.altKey || e.key.length !== 1) {
      // Backspace, arrows and shortcuts act once the held keys landed.
      if (burst && !burst.confirmed && !PASSIVE_KEYS.has(e.key)) finish(now);
      return;
    }

    const gapLimit = burst?.confirmed ? SCAN_END_SILENCE_MS : BURST_GAP_MS;
    if (burst && now - burst.lastKeyAt >= gapLimit) finish(now);
    const landed = landedOnSilence;
    landedOnSilence = null;
    if (!burst && landed && now - landed.burst.lastKeyAt < BURST_GAP_MS) {
      writeField(landed.burst.field, landed.value, landed.caret);
      burst = landed.burst;
    }
    if (!burst) {
      burst = {
        typed: "",
        physical: "",
        confirmed: false,
        field: focusedTextField(),
      };
    }

    burst.typed += e.key;
    burst.physical += usCharFromCode(e) ?? e.key;
    burst.lastKeyAt = now;
    if (burst.typed.length >= CONFIRM_KEYS) burst.confirmed = true;

    // Keys bound for a text field wait until the burst turns out to be a
    // person typing, and a scanner's keys never reach the page.
    if (burst.confirmed || burst.field) {
      swallow(e);
      clearTimeout(endTimer);
      endTimer = burst.confirmed
        ? setTimeout(() => finish(performance.now()), SCAN_END_SILENCE_MS)
        : setTimeout(
            () => finish(performance.now(), { onSilence: true }),
            BURST_GAP_MS
          );
    }
  };

  // A click moves the caret or the focus, so held keys land first, where they
  // were typed.
  const handlePointerDown = () => {
    if (burst && !burst.confirmed) finish(performance.now());
  };

  return {
    handleKeyDown,
    handlePointerDown,
    dispose: () => clearTimeout(endTimer),
  };
};

// onScan gets the code as the laptop typed it and as a US scanner meant it
// (one entry when they match), so the caller can parse whichever is valid.
export const useBarcodeScanner = ({ enabled, onScan }) => {
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;

  // Window capture phase runs before other key handlers and needs no focus in
  // the page.
  useEffect(() => {
    if (!enabled) return undefined;

    const detector = createScanDetector((payloads) =>
      onScanRef.current(payloads)
    );
    window.addEventListener("keydown", detector.handleKeyDown, {
      capture: true,
    });
    window.addEventListener("pointerdown", detector.handlePointerDown, {
      capture: true,
    });
    return () => {
      window.removeEventListener("keydown", detector.handleKeyDown, {
        capture: true,
      });
      window.removeEventListener("pointerdown", detector.handlePointerDown, {
        capture: true,
      });
      detector.dispose();
    };
  }, [enabled]);
};
